import {
  IBidRequest,
  IBidResponse,
  IBidsBackHandlerResponse,
  IPrebid,
  IPrebidAdUnit
} from '../../../typings/IPrebid'
import {
  BID_REQUEST_WAIT,
  CPM_TOFIXED_LIMIT,
  DEFAULT_MAX_DURATION,
  DEFAULT_MIN_DURATION,
  DEFAULT_OUTSTREAM_MIN_DURATION,
  DEFAULT_OUTSTREAM_MAX_DURATION
} from '../../constants/video'
import { IndexExchangeVideo } from '../../enums/VideoBidder'
import {
  API,
  DeliveryMethod,
  Linearity,
  Protocol,
  Placement
} from '../../enums/OpenRTB'
import VideoBidRequest from './VideoBidRequest'
import { VideoBidder, OutstreamBidder } from '../../enums/Bidder'
import { IVideoAdTagOptions } from '../../../typings/IVideoOptions'
import { LiftTest } from '../../liftTest/LiftTest'
import { getQueryParam } from '../../helpers/getQueryParam'
import { AuctionHouse } from '../../auctionHouse/AuctionHouse'
import { Slot } from '../../slots/Slot'
import { MediaType } from '../../enums/MediaType'
import WebModel from '../../models/WebModel'

type ITestBidKeys = keyof typeof VideoBidder
export type ITestBids = { [key in ITestBidKeys]: boolean }

export default class VideoPrebidManager {
  public readonly options: IVideoAdTagOptions
  public readonly overrides: any
  public readonly prebid: IPrebid
  public testBids: ITestBids
  public readonly isOutstream: boolean

  constructor(
    options: IVideoAdTagOptions,
    private readonly webModel: WebModel | IWebViewModel,
    prebid = window.pbjs,
    private auctionHouse: AuctionHouse,
    private readonly liftTest: LiftTest,
    overrides: any = {}
  ) {
    this.options = options
    this.overrides = overrides
    this.prebid = prebid
    this.isOutstream = this.options.placement === Placement.article

    this.testBids = VideoPrebidManager.getTestBids()
    const tBidKeys = Object.keys(this.testBids).filter(
      (bidder) => this.testBids[bidder]
    )

    const videoDebuggingEnabled = !!tBidKeys.length
    if (videoDebuggingEnabled) {
      prebid.setConfig({
        debugging: {
          bidders: tBidKeys,
          adUnitCode: 'content_2_outstream_desktop',
          bids: tBidKeys.map((bidder) => {
            return {
              bidder,
              cpm: 20
            }
          }),
          enabled: videoDebuggingEnabled
        }
      })
    }
  }

  static getTestBids(): ITestBids {
    const qp = getQueryParam('video_test_bids', window.location.search) || ''
    return Object.keys(VideoBidder).reduce((acc, key) => {
      return { ...acc, [key]: qp.indexOf(key) !== -1 }
    }, {}) as ITestBids
  }

  /**
   * GAM requires bids to be properly named/formatted to include the
   * the CPM with a key including the bidderCode. The method
   * formats the highest bid in the auction house
   *  and returns the formatted DFP custom params.
   *
   * Example output:
   * {
   *   formattedBid: {
   *     hb_pb: 5.00,
   *     hb_uuid: 'abcd12345efgh6789',
   *     hb_bidder: 'AOL'
   *   },
   *   highBid: IBidResponse
   * }
   */
  public getFormattedBidFromAuctionHouse(): {
    formattedBid: {}
    highBid?: IBidResponse
  } {
    const spoofSlot = {
      id: this.options.adunitId
    } as Slot
    const highestPoolBid = this.auctionHouse.popHighestBidForSlot(spoofSlot)

    if (!highestPoolBid) {
      return this.webModel.google
        ? { formattedBid: { hb_bid: 0 } }
        : { formattedBid: { error: 'No bids returned', maxBid: 0 } }
    }

    return {
      formattedBid: this.formatBidAsDFPCustomParams(highestPoolBid),
      highBid: highestPoolBid
    }
  }

  // This will run a prebid auction for a video ad unit. The results will end up in the auctionHouse.
  public async runVideoAuction(): Promise<IBidsBackHandlerResponse> {
    return new Promise((resolve, reject) => {
      this.prebid.bidderSettings = {
        ...this.prebid.bidderSettings,
        ...this.bidderSettings
      }
      this.prebid.que.push(() => {
        this.prebid.requestBids({
          adUnits: [this.adUnits],
          auctionId: MediaType.video,
          bidsBackHandler: (namespacedBids: IBidsBackHandlerResponse = {}) => {
            resolve(namespacedBids)
          },
          timeout: BID_REQUEST_WAIT
        })
      })
    })
  }

  get adUnits(): IPrebidAdUnit {
    const playerSize: AdSize = [
      this.options.contentWidth,
      this.options.contentHeight
    ]
    return {
      bids: this.bidRequests,
      code: this.options.adunitId,
      mediaTypes: {
        video: {
          api: [API.VPAID_1_0, API.VPAID_2_0],
          context: this.isOutstream ? 'outstream' : 'instream',
          delivery: [DeliveryMethod.progressive],
          linearity: Linearity.linear,
          maxduration: this.isOutstream
            ? DEFAULT_OUTSTREAM_MAX_DURATION
            : DEFAULT_MAX_DURATION,
          minduration: this.isOutstream
            ? DEFAULT_OUTSTREAM_MIN_DURATION
            : DEFAULT_MIN_DURATION,
          mimes: this.options.mimes,
          placement: this.isOutstream ? Placement.article : Placement.stream,
          playerSize,
          protocols: [
            Protocol.VAST_2_0,
            Protocol.VAST_3_0,
            Protocol.VAST_2_0_wrapper,
            Protocol.VAST_3_0_wrapper,
            Protocol.VAST_4_0,
            Protocol.VAST_4_0_wrapper
          ],
          sizes: [playerSize],
          skip: this.isOutstream ? 0 : 1,
          startdelay: 0
        }
      }
    }
  }

  get bidderSettings(): object {
    return {
      [IndexExchangeVideo.id]: {
        adserverTargeting: [
          {
            key: 'hb_vpmp_ixVideo',
            val(bidResponse) {
              return bidResponse.hb_vpmp_ixVideo
            }
          }
        ]
      }
    }
  }

  get bidRequests(): IBidRequest[] {
    const bidRequest = new VideoBidRequest(
      this.options,
      this.webModel,
      this.overrides,
      this.testBids
    )
    let req: IBidRequest[] = []

    // Note: These are in the order we want them to be requested. Do not reorder unless you know what's up.
    if (this.shouldGetBid('indexExchange')) {
      req.push(bidRequest.indexExchange)
      req.push(bidRequest.roundel)
    }

    if (this.shouldGetBid('uam')) {
      req.push(bidRequest.amazon)
    }

    if (this.shouldGetBid('gumgum')) {
      req.push(bidRequest.gumgum)
    }

    if (this.shouldGetBid('oneVideo')) {
      req.push(bidRequest.oneVideo)
    }

    if (this.shouldGetBid('pubmatic')) {
      req.push(bidRequest.pubmatic)
    }

    // If active lift test, remove bidder
    if (
      this.liftTest.bidder &&
      this.liftTest.isVideo() &&
      this.liftTest.isPrebid()
    ) {
      const bidder = this.liftTest.bidder
      req = req.filter((bidRequest) => bidRequest.bidder !== bidder)
    }

    req.unshift(bidRequest.S2S)

    return req
  }

  private shouldGetBid(bidder: string): boolean {
    if (this.isOutstream) {
      return !!this.webModel[bidder] && !!OutstreamBidder[bidder]
    } else {
      return !!this.webModel[bidder] && !!VideoBidder[bidder]
    }
  }

  public formatBidAsDFPCustomParams(bid: IBidResponse) {
    /**
     * HBBid records the original bid value, pre-bucketing. This is used
     * to analyze the granularity of the buckets and how much discrepancy
     * they currently produce.
     */
    let hbBid
    if (bid && typeof bid.cpm === 'number') {
      const maxBid = 200
      hbBid = bid.cpm < maxBid ? bid.cpm.toFixed(CPM_TOFIXED_LIMIT) : '200.00'
    } else {
      hbBid = 'no_bid'
    }

    let bidderCode = bid.s2sBidder
      ? `${bid.s2sBidder}_s2s`
      : ((bid.bidderCode as unknown) as string)
    /**
     * We are limiting the length of bidderCode so that we don't exceed
     * the length of limit of GAM key/value keys.
     */
    const substrLength = 20
    bidderCode = bidderCode.substring(0, substrLength)

    return {
      ['hb_bid']: hbBid,
      [`hb_pb`]: bid.adserverTargeting.hb_pb,
      [`hb_ub`]: bid.adserverTargeting.hb_ub,
      [`hb_uuid`]: bid.videoCacheKey,
      [`hb_bidder`]: bidderCode
    }
  }
}
